import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, withLatestFrom } from 'rxjs';

import { CustomerDetailsEventsActions } from './customer-details-events.actions';
import { selectFiltersPayload, selectTableState, selectDetails } from './customer-details-events.reducer';
import { CustomersApiService } from '../../../api/customers-api.service';
import { Events } from '../../../../../network-monitoring/events-monitor/events-monitor.types';
import type { CustomerEventDeviceAssignmentForAPI } from '../../../api/customers-api.type';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { DurationPipe } from 'src/app/shared/pipes/duration.pipe';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import {
  buildEventsPayload,
  getEventDeviceAssignment,
  getSelectedFullAccountNumber
} from '../customer-details-events.helpers';
import { multiplierWithUom } from '../../../../../../shared/utils';

const durationPipe = new DurationPipe();

const DEVICE_ASSIGNMENT_TEXT: Record<CustomerEventDeviceAssignmentForAPI, string> = {
  Current: TranslateRegistry.get.CUSTOMERS_DETAILS.TABLE.CURRENT_ASSIGNMENT,
  Past: TranslateRegistry.get.CUSTOMERS_DETAILS.TABLE.PAST_ASSIGNMENT
};

export const getEvents$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    customersApiService = inject(CustomersApiService),
    timezoneService = inject(TimezoneService),
    translateService = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(CustomerDetailsEventsActions.getEvents),
      withLatestFrom(store.select(selectFiltersPayload), store.select(selectTableState), store.select(selectDetails)),
      exhaustMap(([, filters, tableState, details]) => {
        const payload = buildEventsPayload(filters, tableState, details, timezoneService);
        const selectedAccount = getSelectedFullAccountNumber(filters);

        return customersApiService.getDetailsEvents(payload).pipe(
          map(events => {
            const { items, totalCount } = events;
            const data: Events[] = items.map(event => {
              const deviceAssignment = getEventDeviceAssignment(
                event,
                selectedAccount,
                details?.servicePointsByDeviceUID
              );
              return {
                ...event,
                deviceAssignment,
                lastDetectedUtc: timezoneService.formatLong(event.lastDetectedUtc),
                startedAtUtc: timezoneService.formatLong(event.startedAtUtc),
                endedAtUtc: event.endedAtUtc ? timezoneService.formatLong(event.endedAtUtc) : null,
                duration: durationPipe.transform(event.lastDetectedUtc, event.endedAtUtc),
                statusText: event.status,
                deviceAssignmentText: translateService.instant(DEVICE_ASSIGNMENT_TEXT[deviceAssignment]),
                unit: multiplierWithUom(event.multiplier, event.unitOfMeasure)
              };
            });
            return CustomerDetailsEventsActions.getEventsSuccess({ eventsData: { data, total: totalCount } });
          }),
          catchError(() => of(CustomerDetailsEventsActions.getEventsFailure({ error: 'Failed to load events' })))
        );
      })
    ),
  { functional: true }
);
